import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type {
  BioAgent,
  BioTerminal,
  BioTerminalCardsFilters,
  BioTerminalCardsResponse,
  CreateBioAgentDto,
  CreateBioTerminalDto,
  UpdateBioAgentDto,
  UpdateBioTerminalDto,
} from '../types/bio.types';

export const bioKeys = {
  all: ['bio'] as const,
  agents: () => [...bioKeys.all, 'agents'] as const,
  terminals: () => [...bioKeys.all, 'terminals'] as const,
  cards: (filters: BioTerminalCardsFilters) =>
    [...bioKeys.all, 'terminal-cards', filters] as const,
};

/**
 * Опции для справочников био: меню читает их только при праве на раздел,
 * а живое обновление статуса нужно лишь на экране настроек.
 */
interface BioListOptions {
  enabled?: boolean;
  refetchInterval?: number | false;
}

/** Агенты объектов. Онлайн-статус живёт по heartbeat. */
export const useBioAgents = (options: BioListOptions = {}) =>
  useQuery({
    queryKey: bioKeys.agents(),
    queryFn: async (): Promise<BioAgent[]> => apiClient.getBioAgents(),
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval ?? false,
    staleTime: 30 * 1000,
  });

export const useBioTerminals = (options: BioListOptions = {}) =>
  useQuery({
    queryKey: bioKeys.terminals(),
    queryFn: async (): Promise<BioTerminal[]> => apiClient.getBioTerminals(),
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval ?? false,
    staleTime: 30 * 1000,
  });

export const useCreateBioAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBioAgentDto) => apiClient.createBioAgent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bioKeys.agents() });
    },
  });
};

export const useUpdateBioAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBioAgentDto }) =>
      apiClient.updateBioAgent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bioKeys.agents() });
    },
  });
};

export const useRotateBioAgentToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.rotateBioAgentToken(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bioKeys.agents() });
    },
  });
};

export const useCreateBioTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBioTerminalDto) =>
      apiClient.createBioTerminal(data),
    onSuccess: () => {
      // Постановка терминала на агента ставит команду sync_time — очередь
      // агента видна в его карточке, поэтому обновляем и агентов.
      queryClient.invalidateQueries({ queryKey: bioKeys.all });
    },
  });
};

export const useUpdateBioTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBioTerminalDto }) =>
      apiClient.updateBioTerminal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bioKeys.all });
    },
  });
};

/** Прямая команда терминалу (пока из UI нужна только sync_time). */
export const useCreateBioCommand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      terminal_id: string;
      command_type: 'capture_face' | 'sync_time' | 'reboot';
      employee_id?: string;
    }) => apiClient.createBioCommand(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bioKeys.terminals() });
    },
  });
};

/** Карточки, прочитанные агентом с терминалов (экран сопоставления). */
export const useBioTerminalCards = (filters: BioTerminalCardsFilters = {}) =>
  useQuery({
    queryKey: bioKeys.cards(filters),
    queryFn: async (): Promise<BioTerminalCardsResponse> =>
      apiClient.getBioTerminalCards(filters),
  });

export const useMapBioTerminalCard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      cardId,
      employeeId,
    }: {
      cardId: string;
      employeeId: string;
    }) => apiClient.mapBioTerminalCard(cardId, employeeId),
    onSuccess: () => {
      // Сопоставление создаёт регистрацию и правит табель — обновляем всё bio.
      queryClient.invalidateQueries({ queryKey: bioKeys.all });
    },
  });
};
